import {
  TpCustomAxisDefinition,
  renderGrid,
  renderXaxisNumerical,
  renderYaxisNumerical,
} from '../../zoompan-canvas/renderAxes';
import { getBoundData, getSecondBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
  TpZoomPanelCoordMapper,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable } from '../../../../data/interfaces';
import { getColumnInfo } from '../../../../data/analyticsDataSlice';
import { renderLegendItems, renderXLabel, renderYLabel, TpLegendItem } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import {
  createColorMappingInfo,
  getMappedColorLegendInfo,
  getMappedColorStringDataPoint,
} from '../../helpers/colorMappingInfo';
import {
  chartDims,
  convertColumnDataListToDisplayString,
  generateChartTitle,
  isSelectedItem,
} from '../../helpers/misc';
import { getRange } from '../../helpers/axisDef';
import { currentTheme, getStandardDataColor, getThemedGrayFraction } from '../../../themes/themes';
import { TpHoverInfo, TpHoverRenderContext } from '../../zoompan-canvas/ZoomPanCanvas';
import { TpDashboardGlobalChartConfig } from '../../dashboard/interfaces';
import { clipToRenderedRange } from '../../zoompan-canvas/helpers';

export function generateXAxisRenderer(customXAxis: TpCustomAxisDefinition | null) {
  if (!customXAxis) return renderXaxisNumerical;

  return (renderContext: TpAreaRenderContext) => {
    const { mapx, mapy, mapinvx, mapinvy, areaXMin, areaXMax, pixelRatio, mapsize } = renderContext;
    const ctx = renderContext.canvasContext;
    ctx.strokeStyle = getThemedGrayFraction(0.65).toCanvasString();
    ctx.fillStyle = currentTheme.colors.text.toCanvasString();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    renderContext.setFont(9);

    let lastXPos = -1e99;
    for (const labelInfo of customXAxis.labels) {
      const xPos = mapx(labelInfo.value);
      const textWidth = ctx.measureText(labelInfo.text).width + pixelRatio * 3;
      if (xPos - textWidth / 2 > lastXPos) {
        ctx.fillText(labelInfo.text, xPos, mapy(0.5));
        lastXPos = xPos + textWidth / 2;
      }
    }
  };
}

interface TpSampleInfo {
  sampleId: string;
  isPresent: boolean;
  sampleIdx: number;
  colorString: string;
  points: {
    pointIdx: number;
    sortVal: number;
    x: number;
    y: number;
  }[];
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  // nomenclature convention:
  // samples = the parent level of the longitudinal data = primary datatable
  // points = the individual measurements level = secondary datatable
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding); // for samples level
  const boundData2 = getSecondBoundData(chartTypeManifest, dataContext, dataBinding); // for data points level
  const dataTable: TpDataTable = boundData.dataTable;

  const dataSampleItemIds = boundData.bindings.itemId.data;
  const getSampleItemIndexById = boundData.getItemIndexById;
  const selectedSamplesMap = boundData.canonicalTypeInfo.selectedItemsMap;

  const dataPointSort = boundData2.bindings.sort.data;
  const dataPointX = boundData2.bindings.x.data;
  const dataPointY = boundData2.bindings.y.data;
  const dataPointItemIds = boundData2.bindings.itemId.data;
  const dataPointSampleIds = convertColumnDataListToDisplayString(
    getColumnInfo(boundData2.dataTable, boundData2.bindings.linkField.columnId),
    boundData2.bindings.linkField.data,
  );

  const dataSampleHoverLabel = boundData.bindings.hoverLabel?.data;
  const colInfoSampleHoverLabel = boundData.bindings.hoverLabel?.columnId
    ? getColumnInfo(dataTable, boundData.bindings.hoverLabel?.columnId)
    : null;
  const colInfoPointY = getColumnInfo(boundData2.dataTable, boundData2.bindings.y.columnId);

  const nameX = boundData2.bindings.x.columnInfo.name;
  const nameY = boundData2.bindings.y.columnInfo.name;
  const title = generateChartTitle(dataContext, dataBinding, config, `${nameX} vs ${nameY}`);
  const [minValX, maxValX] = getRange(dataPointX, 0.05);
  const [minValY, maxValY] = getRange(dataPointY, 0.05);

  const opacity = config.opacity;
  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;
  const colorMappingInfo = createColorMappingInfo(config, boundData, 'color', opacity, globalColorMappingConfig);
  const standardFillColor = getStandardDataColor().opacity(opacity).toCanvasString();
  const unselColor1 = getThemedGrayFraction(0.65)
    .opacity(0.25 * opacity)
    .toCanvasString();

  // Assemble the samples data
  const samples: TpSampleInfo[] = [];
  const samplesMap: { [id: string]: TpSampleInfo } = {};
  for (let i = 0; i < dataPointX.length; i++) {
    const pointSampleId = dataPointSampleIds[i];
    if (!samplesMap[pointSampleId]) {
      const sampleIdx = getSampleItemIndexById(pointSampleId);
      const newSample: TpSampleInfo = {
        sampleId: pointSampleId,
        sampleIdx,
        isPresent: sampleIdx !== undefined,
        points: [],
        colorString: unselColor1,
      };
      samples.push(newSample);
      samplesMap[pointSampleId] = newSample;
    }
    samplesMap[pointSampleId].points.push({
      pointIdx: i,
      sortVal: dataPointSort[i],
      x: dataPointX[i],
      y: dataPointY[i],
    });
  }

  // Determine colors + sorting the sample points by sort value
  for (const sample of samples) {
    if (sample.isPresent) {
      if (colorMappingInfo.hasColors) {
        sample.colorString = getMappedColorStringDataPoint(colorMappingInfo, sample.sampleIdx)[1];
      } else {
        sample.colorString = standardFillColor;
      }
      if (!isSelectedItem(selectedSamplesMap, sample.sampleId)) sample.colorString = unselColor1;
    }
    sample.points.sort((s1, s2) => s1.sortVal - s2.sortVal);
  }

  const pointSizeFactor = config.pointSizeFactor;

  function renderPoints(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    if (!config.hideAxes) renderGrid(renderContext, true, true, config.customXAxis);

    ctx.strokeStyle = getStandardDataColor()
      .opacity(opacity * 0.3)
      .toCanvasString();
    ctx.lineWidth = renderContext.pixelRatio * 0.75;

    for (const sample of samples) {
      let firstPoint = true;
      ctx.strokeStyle = sample.colorString;
      ctx.beginPath();
      for (const point of sample.points) {
        // draw lines
        if (!isNaN(point.x) && !isNaN(point.y)) {
          const scr_x = mapx(point.x);
          const scr_y = mapy(point.y);
          if (firstPoint) ctx.moveTo(scr_x, scr_y);
          else ctx.lineTo(scr_x, scr_y);
          firstPoint = false;
        }
      }
      if (!firstPoint) ctx.stroke();
      for (const point of sample.points) {
        // draw points
        if (!isNaN(point.x) && !isNaN(point.y)) {
          ctx.fillStyle = sample.colorString;
          const scr_x = mapx(point.x);
          const scr_y = mapy(point.y);
          ctx.beginPath();
          ctx.arc(scr_x, scr_y, pixelConv(2 * pointSizeFactor), 0, 2 * Math.PI);
          ctx.fill();
        }
      }
    }
  }

  function renderOverlay(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;
  }

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    if (colorMappingInfo.hasColors) legendItems.push(getMappedColorLegendInfo(colorMappingInfo));
    renderLegendItems(renderContext, legendItems);
  }

  function customHoverRenderer(
    hoverInfo: TpHoverInfo,
    hoverContext: TpHoverRenderContext,
    ctx: CanvasRenderingContext2D,
    mapX: TpZoomPanelCoordMapper,
    mapY: TpZoomPanelCoordMapper,
    pixelRatio: number,
  ) {
    clipToRenderedRange(ctx, mapX, mapY);

    const sample = samplesMap[hoverInfo.itemId];
    if (!sample) return;

    ctx.lineWidth = pixelRatio * 3.75;

    let firstPoint = true;
    ctx.strokeStyle = sample.colorString;
    ctx.beginPath();
    for (const point of sample.points) {
      // draw lines
      if (!isNaN(point.x) && !isNaN(point.y)) {
        const scr_x = mapX.map(point.x);
        const scr_y = mapY.map(point.y);
        if (firstPoint) ctx.moveTo(scr_x, scr_y);
        else ctx.lineTo(scr_x, scr_y);
        firstPoint = false;
      }
    }
    if (!firstPoint) ctx.stroke();
    for (const point of sample.points) {
      // draw points
      if (!isNaN(point.x) && !isNaN(point.y)) {
        ctx.fillStyle = sample.colorString;
        const scr_x = mapX.map(point.x);
        const scr_y = mapY.map(point.y);
        ctx.beginPath();
        ctx.arc(scr_x, scr_y, pixelRatio * (4 * pointSizeFactor), 0, 2 * Math.PI);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  const hasLegend = colorMappingInfo.hasColors && !config.hideColorLegend;
  const legendBottomSize = hasLegend ? 38 : 0;

  const padding = chartDims.padding;

  const zpLogicalRangeX = createLogicalRange(minValX, maxValX);
  const zpLogicalRangeY = createLogicalRange(minValY, maxValY);
  const zpRenderRangeX = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.yAxisWidth),
    RenderMaxDiffAbs(padding + 0),
  );
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(
      padding + (!config.hideAxes ? chartDims.xAxisHeight + chartDims.xLabelHeight : 0) + legendBottomSize,
    ),
  );

  const areaPoints: TpChartAreaDefinition = {
    id: 'points',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    canAreaSelect: 'xy',
    render: renderPoints,
    renderOverlay,
  };

  const chartAreas: TpChartAreaDefinition[] = [areaPoints];

  if (!config.hideAxes)
    chartAreas.push({
      id: 'xaxis',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + chartDims.xAxisHeight + chartDims.xLabelHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + chartDims.xLabelHeight + legendBottomSize),
      ),
      zoomPanX: true,
      render: generateXAxisRenderer(config.customXAxis),
    });
  if (!config.hideAxes)
    chartAreas.push({
      id: 'xlabel',
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + chartDims.xLabelHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      render: (renderContext: TpAreaRenderContext) => renderXLabel(renderContext, nameX),
    });
  if (!config.hideAxes)
    chartAreas.push({
      id: 'yaxis',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(
        RenderMinDiffAbs(padding + 4),
        RenderMinDiffAbs(padding + 4 + chartDims.yAxisWidth),
      ), // offset of Y=4 to make axes overlap
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderYaxisNumerical,
    });
  chartAreas.push({
    id: 'ylabel',
    renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
    renderRangeY: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMinDiffAbs(padding + chartDims.headerHeight)),
    render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, !config.hideAxes ? nameY : '', title),
  });

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaPoints,
    dataTable,
    samples,
    getSampleItemIndexById,
    customHoverRenderer,
    colInfoSampleHoverLabel,
    dataSampleHoverLabel,
    nameY,
    colInfoPointY,
    dataPointY,
    title,
  };
}
